import React from 'react';
import { Link } from 'react-router-dom';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { ChevronDown, FileText, Phone, Mail } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const TermsPage = () => {
  // Set document meta tags
  useDocumentMeta({
    title: 'Terms & Conditions | Kalamata Airport Taxi',
    description: 'Kalamata Airport Taxi terms and conditions for transfer bookings in Messenia, Greece. Read our booking policies, cancellation terms, and service conditions.',
    canonical: 'https://kalamataairporttaxi.com/terms'
  });

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[40vh] flex items-center bg-[#2d232e] overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
              backgroundSize: '40px 40px'
            }} />
          </div>
          
          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#36827f]/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#36827f]/10 rounded-full blur-3xl" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative pt-32 pb-16 w-full">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-white/50 mb-8">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
              <span className="text-[#36827f]">Terms & Conditions</span>
            </nav>

            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-xl bg-[#36827f]/20 flex items-center justify-center">
                <FileText className="h-8 w-8 text-[#36827f]" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-white">Terms & Conditions</h1>
                <p className="text-white/50 mt-1">Last updated: January 2026</p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Important Notice */}
            <div className="bg-[#36827f]/10 border border-[#36827f]/20 rounded-2xl p-6 mb-12">
              <p className="text-[#2d232e] m-0">
                By booking a transfer with Kalamata Airport Taxi, you agree to these terms and conditions. 
                Please read them carefully before making a reservation.
              </p>
            </div>

            <div className="space-y-10">
              
              {/* Section 1 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">1</span>
                  Booking & Confirmation
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>All reservations depend on availability and require confirmation from our team.</li>
                  <li>Your booking becomes valid once you receive our confirmation email.</li>
                  <li>Please review your confirmation details carefully and let us know immediately if anything needs correction.</li>
                  <li>We suggest making reservations 24-48 hours ahead, particularly in the busy summer months (June-September).</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">2</span>
                  Pricing & Payment
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Fixed Rates:</strong> Prices are set at booking time with no surprise charges or hidden costs.</li>
                  <li><strong>Currency:</strong> All amounts are quoted in Euros (€).</li>
                  <li><strong>Accepted Payments:</strong> We take credit cards (Visa, Mastercard), debit cards, and cash.</li>
                  <li><strong>When to Pay:</strong> You can pay in full or leave a deposit when booking. Any remaining balance is due after your transfer.</li>
                  <li><strong>What's Covered:</strong> Your fare includes the vehicle, driver, fuel, tolls, taxes, meet & greet, and flight tracking.</li>
                </ul>
              </div>

              {/* Section 3 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">3</span>
                  Cancellation Policy
                </h2>
                <div className="bg-gray-50 rounded-xl p-6 mb-4">
                  <ul className="list-disc pl-6 text-gray-600 space-y-2 m-0">
                    <li><strong>Free Cancellation:</strong> Cancel more than 24 hours before pickup and receive a complete refund.</li>
                    <li><strong>Late Cancellation:</strong> Cancelling within 24 hours of your pickup time may result in a charge of up to 50%.</li>
                    <li><strong>No-Show:</strong> If you don't show up without notifying us, no refund will be issued.</li>
                  </ul>
                </div>
                <p className="text-gray-600">
                  To cancel your booking, reach out via email at <a href="mailto:info@kalamataairporttaxi.com" className="text-[#36827f] hover:underline">info@kalamataairporttaxi.com</a> or 
                  call us at <a href="tel:+306932421170" className="text-[#36827f] hover:underline">+30 693 242 1170</a>.
                </p>
              </div>

              {/* Section 4 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">4</span>
                  Modifications
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>You can modify your date, time, or pickup point up to 12 hours before your scheduled transfer.</li>
                  <li>Changing your route may affect the price. We'll inform you of any difference.</li>
                  <li>Last-minute changes (within 12 hours) depend on availability and might incur extra fees.</li>
                </ul>
              </div>

              {/* Section 5 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">5</span>
                  Flight Delays & Monitoring
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>We track all arriving flights in real-time for airport pickups.</li>
                  <li>If your flight arrives late, we adjust your pickup time automatically at no additional cost.</li>
                  <li>Please include your flight number when booking so we can monitor it.</li>
                  <li>Up to 60 minutes of waiting time after landing is included in your fare.</li>
                </ul>
              </div>

              {/* Section 6 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">6</span>
                  Waiting Time
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li><strong>Airport Pickups:</strong> 60 minutes complimentary waiting from when your flight lands.</li>
                  <li><strong>Port Pickups:</strong> 30 minutes complimentary waiting from ferry arrival.</li>
                  <li><strong>Hotel/Other Pickups:</strong> 15 minutes complimentary waiting from your scheduled time.</li>
                  <li>Extra waiting beyond these periods may be charged at €20 per 30 minutes.</li>
                </ul>
              </div>

              {/* Section 7 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">7</span>
                  Passenger Responsibilities
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Please be at the pickup location on time.</li>
                  <li>All passengers must wear seatbelts throughout the journey.</li>
                  <li>Let us know beforehand about any special needs or mobility requirements.</li>
                  <li>Smoking is not permitted in our vehicles.</li>
                  <li>Passengers will be responsible for any damage caused to the vehicle.</li>
                </ul>
              </div>

              {/* Section 8 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">8</span>
                  Luggage
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Standard luggage is included based on the capacity of your chosen vehicle.</li>
                  <li>If you have extra or oversized bags, please mention this when booking.</li>
                  <li>Sports gear (golf clubs, bikes, etc.) should be declared ahead of time.</li>
                </ul>
              </div>

              {/* Section 9 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">9</span>
                  Child Seats
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>We provide child and baby seats at no extra charge upon request.</li>
                  <li>Please tell us the ages of your children when booking so we can prepare suitable seats.</li>
                  <li>Availability of child seats should be confirmed at the time of reservation.</li>
                </ul>
              </div>

              {/* Section 10 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">10</span>
                  Liability
                </h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>Kalamata Airport Taxi holds all required licenses and insurance for passenger transport.</li>
                  <li>We cannot be held responsible for delays due to traffic, weather, or other factors outside our control.</li>
                  <li>We are not liable for items left behind in the vehicle, although we will do our best to help recover them.</li>
                  <li>Our maximum liability for any claim is limited to the price of your booking.</li>
                </ul>
              </div>

              {/* Section 11 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">11</span>
                  Force Majeure
                </h2>
                <p className="text-gray-600">
                  We are not responsible for service failures caused by events beyond our control, such as 
                  natural disasters, strikes, civil disturbances, government actions, or health emergencies. In these situations, we will offer to reschedule or provide a full refund.
                </p>
              </div>

              {/* Section 12 */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">12</span>
                  Governing Law
                </h2>
                <p className="text-gray-600">
                  These terms are governed by Greek law. Any legal disputes will fall under the exclusive 
                  jurisdiction of the courts in Kalamata, Messenia, Greece.
                </p>
              </div>

              {/* Section 13 - Contact */}
              <div>
                <h2 className="text-xl font-bold text-[#2d232e] mb-4 flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-[#36827f] text-white flex items-center justify-center text-sm font-bold">13</span>
                  Contact Information
                </h2>
                <p className="text-gray-600 mb-4">
                  For any questions regarding these terms, please contact us:
                </p>
                <div className="bg-gray-50 rounded-2xl p-6">
                  <p className="font-bold text-[#2d232e] text-lg mb-4">Kalamata Airport Taxi</p>
                  <div className="space-y-3">
                    <a href="tel:+306932421170" className="flex items-center gap-3 text-gray-600 hover:text-[#36827f] transition">
                      <div className="w-10 h-10 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                        <Phone className="w-5 h-5 text-[#36827f]" />
                      </div>
                      <span>+30 693 242 1170</span>
                    </a>
                    <a href="mailto:info@kalamataairporttaxi.com" className="flex items-center gap-3 text-gray-600 hover:text-[#36827f] transition">
                      <div className="w-10 h-10 rounded-lg bg-[#36827f]/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-[#36827f]" />
                      </div>
                      <span>info@kalamataairporttaxi.com</span>
                    </a>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TermsPage;
